import { Component, type ErrorInfo, type ReactNode } from "react";
import { Link, Outlet, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

/**
 * A throw while rendering one screen must not take the whole portal down.
 *
 * React answers an uncaught render error by unmounting the entire tree, so a single broken screen
 * leaves a blank page: no menu, no message, no way out but a reload. A boundary stops the unmount at
 * itself and puts a panel in that one place instead.
 *
 * Where it sits is the whole point. Inside the shell, around the routed content: the menu stays on
 * screen, so the next click leads somewhere. Around the shell it would save nothing worth saving.
 *
 * The reader is told that the screen failed, and nothing more. The error and the component stack go
 * to the console, for us: a stack trace on screen names internal files and helps nobody who reads it.
 *
 * Only a class can catch, which is why this one is a class. The panel is a function component, so it
 * can read the translations the normal way.
 */

type Props = {
  children?: ReactNode;
  /** Changing this clears a shown panel. The route path is what the portal passes. */
  resetKey?: string;
};

type State = { failed: boolean };

export class ErrorBoundary extends Component<Props, State> {
  state: State = { failed: false };

  /** Runs during the failing render, so it may only compute state. The error is read below. */
  static getDerivedStateFromError(): State {
    return { failed: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("[ui] screen failed to render", error, info.componentStack);
  }

  /**
   * A changed key means the reader asked for different content — a menu click, as a rule. The panel
   * belongs to the screen that failed; left standing it would swallow every screen after it, and the
   * menu would look dead.
   */
  componentDidUpdate(prev: Props) {
    if (this.state.failed && prev.resetKey !== this.props.resetKey) this.setState({ failed: false });
  }

  private readonly retry = () => this.setState({ failed: false });

  render() {
    return this.state.failed ? <ErrorPanel onRetry={this.retry} /> : this.props.children;
  }
}

/**
 * The boundary as a layout route: it wraps whatever the shell routes into its outlet, and the path it
 * is mounted under is also the reset key, so leaving a broken screen clears the panel.
 */
export function RouteErrorBoundary() {
  const { pathname } = useLocation();
  return (
    <ErrorBoundary resetKey={pathname}>
      <Outlet />
    </ErrorBoundary>
  );
}

/** Two ways out: render this screen again, or go to a screen known to work. */
function ErrorPanel({ onRetry }: { onRetry: () => void }) {
  const { t } = useTranslation();
  return (
    <div className="card" role="alert">
      <div className="card-h">
        <div>
          <h3>{t("error.boundary.title", "Ovaj deo ekrana se nije prikazao.")}</h3>
          <div className="sub">
            {t("error.boundary.sub", "Ostatak portala radi i meni vodi dalje.")}
          </div>
        </div>
      </div>
      <div className="card-b">
        <div className="empty">
          <p style={{ margin: 0 }}>
            {t("error.boundary.hint", "Pokušajte ponovo ili se vratite na kontrolnu tablu.")}
          </p>
          <div
            style={{
              display: "flex",
              gap: 10,
              justifyContent: "center",
              marginTop: 16,
            }}
          >
            <button type="button" className="btn primary" onClick={onRetry}>
              {t("action.retry", "Pokušaj ponovo")}
            </button>
            <Link className="btn" to="/dashboard">
              {t("nav.dashboard", "Kontrolna tabla")}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
